import React, { useState, useEffect } from 'react';
import { ArrowLeft, Shield, FileText, HelpCircle, Cookie, CheckCircle2, ExternalLink } from 'lucide-react';

export type LegalTabType = 'privacy' | 'terms' | 'cookies' | 'faq';

interface LegalViewProps {
  initialTab?: LegalTabType;
  onBack: () => void;
}

export const LegalView: React.FC<LegalViewProps> = ({ initialTab = 'privacy', onBack }) => {
  const [activeTab, setActiveTab] = useState<LegalTabType>(initialTab);

  useEffect(() => {
    setActiveTab(initialTab);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [initialTab]);

  return (
    <div className="max-w-4xl mx-auto space-y-8 py-4 text-left">
      {/* Top Header & Breadcrumb */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-zinc-800 pb-5">
        <div>
          <button
            onClick={onBack}
            className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors mb-2"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>Volver</span>
          </button>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white">
            Políticas, Privacidad y Términos
          </h1>
          <p className="text-sm sm:text-base text-zinc-400 mt-2 leading-relaxed">
            Información legal, tratamiento de datos RGPD y condiciones del servicio Vidly AI
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center gap-1 bg-zinc-900/80 p-1 rounded-xl border border-zinc-800 text-xs overflow-x-auto self-start sm:self-auto">
          <button
            onClick={() => setActiveTab('privacy')}
            className={`px-3 py-1.5 rounded-lg font-medium transition-all whitespace-nowrap flex items-center gap-1.5 ${
              activeTab === 'privacy'
                ? 'bg-zinc-800 text-white shadow-sm'
                : 'text-zinc-400 hover:text-white'
            }`}
          >
            <Shield className="w-3.5 h-3.5 text-zinc-400" />
            <span>Privacidad RGPD</span>
          </button>
          <button
            onClick={() => setActiveTab('terms')}
            className={`px-3 py-1.5 rounded-lg font-medium transition-all whitespace-nowrap flex items-center gap-1.5 ${
              activeTab === 'terms'
                ? 'bg-zinc-800 text-white shadow-sm'
                : 'text-zinc-400 hover:text-white'
            }`}
          >
            <FileText className="w-3.5 h-3.5 text-zinc-400" />
            <span>Términos</span>
          </button>
          <button
            onClick={() => setActiveTab('cookies')}
            className={`px-3 py-1.5 rounded-lg font-medium transition-all whitespace-nowrap flex items-center gap-1.5 ${
              activeTab === 'cookies'
                ? 'bg-zinc-800 text-white shadow-sm'
                : 'text-zinc-400 hover:text-white'
            }`}
          >
            <Cookie className="w-3.5 h-3.5 text-zinc-400" />
            <span>Cookies & AdSense</span>
          </button>
          <button
            onClick={() => setActiveTab('faq')}
            className={`px-3 py-1.5 rounded-lg font-medium transition-all whitespace-nowrap flex items-center gap-1.5 ${
              activeTab === 'faq'
                ? 'bg-zinc-800 text-white shadow-sm'
                : 'text-zinc-400 hover:text-white'
            }`}
          >
            <HelpCircle className="w-3.5 h-3.5 text-zinc-400" />
            <span>Cómo funciona</span>
          </button>
        </div>
      </div>

      {/* TAB CONTENT: PRIVACY & RGPD */}
      {activeTab === 'privacy' && (
        <div className="space-y-6 text-zinc-300 text-xs sm:text-sm leading-relaxed">
          <div className="p-5 rounded-2xl bg-zinc-900/40 border border-zinc-800/80 space-y-3">
            <div className="flex items-center gap-2 text-white font-semibold text-base">
              <Shield className="w-5 h-5 text-zinc-400" />
              <span>Política de Privacidad y Protección de Datos (RGPD UE 2016/679)</span>
            </div>
            <p className="text-zinc-400 text-xs">
              Última actualización: Octubre 2026. Vidly AI cumple rigurosamente con el Reglamento General de Protección de Datos (RGPD) de la Unión Europea y la Ley Orgánica española 3/2018 (LOPDGDD).
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-5 rounded-2xl bg-zinc-900/30 border border-zinc-800/60 space-y-2">
              <h3 className="text-white font-medium text-sm flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-zinc-400" />
                1. Proveedor de IA sin Re-entrenamiento
              </h3>
              <p className="text-zinc-400 text-xs leading-relaxed">
                Utilizamos exclusivamente la API comercial enterprise de Google Gemini. Conforme a los términos contractuales de Google Cloud, tus vídeos, pistas de audio, guiones y transcripciones <strong>nunca se utilizan para entrenar modelos públicos de inteligencia artificial</strong> ni son revisados por personal humano.
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-zinc-900/30 border border-zinc-800/60 space-y-2">
              <h3 className="text-white font-medium text-sm flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-zinc-400" />
                2. Purga y Borrado Automático de Archivos
              </h3>
              <p className="text-zinc-400 text-xs leading-relaxed">
                Los vídeos cargados y sus renders resultantes se almacenan en buckets privados y temporales con ciclo de vida estricto:
              </p>
              <ul className="list-disc pl-4 space-y-1 text-zinc-400 text-xs">
                <li><strong className="text-zinc-200">Plan Gratuito:</strong> Eliminación irreversible en 24 horas.</li>
                <li><strong className="text-zinc-200">Plan Premium:</strong> Retención ampliada de 30 días para volver a descargar.</li>
              </ul>
            </div>
          </div>

          <div className="p-5 rounded-2xl bg-zinc-900/30 border border-zinc-800/60 space-y-3">
            <h3 className="text-white font-medium text-sm">3. Derechos del Interesado (ARCO-POL)</h3>
            <p className="text-zinc-400 text-xs">
              Como usuario residente en la Unión Europea o en cualquier otra jurisdicción con leyes de privacidad vigentes, tienes plenos derechos a:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
              <div className="p-3 rounded-xl bg-zinc-900/50 border border-zinc-800/80 text-xs space-y-1">
                <span className="font-semibold text-zinc-200">Acceso y Portabilidad</span>
                <p className="text-zinc-500 text-[11px]">Descargar tus datos y vídeos generados en formato abierto.</p>
              </div>
              <div className="p-3 rounded-xl bg-zinc-900/50 border border-zinc-800/80 text-xs space-y-1">
                <span className="font-semibold text-zinc-200">Derecho al Olvido</span>
                <p className="text-zinc-500 text-[11px]">Solicitar la supresión total e inmediata de tu cuenta y archivos asociados.</p>
              </div>
              <div className="p-3 rounded-xl bg-zinc-900/50 border border-zinc-800/80 text-xs space-y-1">
                <span className="font-semibold text-zinc-200">Revocación</span>
                <p className="text-zinc-500 text-[11px]">Retirar tu consentimiento de cookies o cerrar sesión en cualquier momento.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB CONTENT: TERMS OF SERVICE */}
      {activeTab === 'terms' && (
        <div className="space-y-6 text-zinc-300 text-xs sm:text-sm leading-relaxed">
          <div className="p-5 rounded-2xl bg-zinc-900/40 border border-zinc-800/80 space-y-3">
            <div className="flex items-center gap-2 text-white font-semibold text-base">
              <FileText className="w-5 h-5 text-zinc-300" />
              <span>Términos y Condiciones de Uso</span>
            </div>
            <p className="text-zinc-400 text-xs">
              Al utilizar Vidly AI, aceptas estos términos y condiciones que rigen el servicio.
            </p>
          </div>

          <div className="space-y-4">
            <div className="p-5 rounded-2xl bg-zinc-900/30 border border-zinc-800/60 space-y-2">
              <h3 className="text-white font-medium text-sm">1. Propiedad Intelectual del Metraje</h3>
              <p className="text-zinc-400 text-xs leading-relaxed">
                Tú eres y serás siempre el único propietario de los derechos de autor de tus vídeos originales y de los archivos resultantes recortados con Vidly AI. Vidly AI no reclama ningún derecho, licencia de explotación perpetua ni titularidad sobre el contenido que proceses.
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-zinc-900/30 border border-zinc-800/60 space-y-2">
              <h3 className="text-white font-medium text-sm">2. Condiciones de la Fase Beta Abierta</h3>
              <p className="text-zinc-400 text-xs leading-relaxed">
                Vidly AI opera temporalmente bajo modalidad de <strong>Beta Abierta limitada a 100 vídeos</strong> con el propósito de entrenar y calibrar la precisión del modelo en la sincronización labial y cortes de audio. El servicio se entrega "tal cual" (*as-is*) y el usuario reconoce que pueden presentarse cortes aproximados o variaciones menores en la duración estimada.
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-zinc-900/30 border border-zinc-800/60 space-y-2">
              <h3 className="text-white font-medium text-sm">3. Facturación y Suscripción Premium</h3>
              <p className="text-zinc-400 text-xs leading-relaxed">
                Los pagos se procesan de forma cifrada a través de Stripe bajo estándares PCI-DSS de Nivel 1. La suscripción mensual puede cancelarse en cualquier instante desde el panel de usuario sin penalizaciones ni permanencia obligatoria.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* TAB CONTENT: COOKIES & ADSENSE */}
      {activeTab === 'cookies' && (
        <div className="space-y-6 text-zinc-300 text-xs sm:text-sm leading-relaxed">
          <div className="p-5 rounded-2xl bg-zinc-900/40 border border-zinc-800/80 space-y-3">
            <div className="flex items-center gap-2 text-white font-semibold text-base">
              <Cookie className="w-5 h-5 text-zinc-400" />
              <span>Política de Cookies y Publicidad de Google AdSense</span>
            </div>
            <p className="text-zinc-400 text-xs">
              Explicación transparente de las cookies utilizadas y cómo funciona la publicidad para financiar el acceso gratuito.
            </p>
          </div>

          <div className="space-y-4">
            <div className="p-5 rounded-2xl bg-zinc-900/30 border border-zinc-800/60 space-y-3">
              <h3 className="text-white font-medium text-sm">¿Cómo utilizamos las cookies?</h3>
              <p className="text-zinc-400 text-xs leading-relaxed">
                Vidly AI clasifica sus cookies en dos categorías exclusivas:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                <div className="p-4 rounded-xl bg-zinc-900/60 border border-zinc-800 text-xs space-y-1.5">
                  <span className="font-semibold text-white">1. Cookies Técnicas y de Sesión</span>
                  <p className="text-zinc-400 text-[11px]">
                    Estrictamente necesarias para mantener iniciada tu sesión segura con Google OAuth, recordar tus preferencias de tema y gestionar la cuota diaria de vídeos. No requieren consentimiento publicitario.
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-zinc-900/60 border border-zinc-800 text-xs space-y-1.5">
                  <span className="font-semibold text-white">2. Cookies Publicitarias (Google AdSense)</span>
                  <p className="text-zinc-400 text-[11px]">
                    Permiten a Google y a sus socios mostrar anuncios discretos para sustentar el coste de servidores FFmpeg y llamadas de IA para los usuarios de la versión gratuita.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-5 rounded-2xl bg-zinc-900/30 border border-zinc-800/60 space-y-3">
              <h3 className="text-white font-medium text-sm">Control y Desactivación de Publicidad Personalizada</h3>
              <p className="text-zinc-400 text-xs leading-relaxed">
                Google utiliza cookies (como las de DoubleClick) para publicar anuncios basados en tus visitas a este u otros sitios web. Tienes total libertad para inhabilitar la personalización:
              </p>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <a
                  href="https://adssettings.google.com"
                  target="_blank"
                  rel="noreferrer"
                  className="btn-secondary text-xs py-2 px-3 flex items-center justify-center gap-1.5"
                >
                  <span>Configuración de Anuncios Google</span>
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <a
                  href="https://www.aboutads.info/choices/"
                  target="_blank"
                  rel="noreferrer"
                  className="btn-secondary text-xs py-2 px-3 flex items-center justify-center gap-1.5"
                >
                  <span>Opt-out en AboutAds.info</span>
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-zinc-900/40 border border-zinc-800 text-xs text-zinc-300">
              💡 <strong>Sin anuncios en Premium:</strong> Si estás suscrito al plan Premium, todos los anuncios y scripts publicitarios quedan inhabilitados en tu navegador automáticamente.
            </div>
          </div>
        </div>
      )}

      {/* TAB CONTENT: FAQ & HOW IT WORKS */}
      {activeTab === 'faq' && (
        <div className="space-y-6 text-zinc-300 text-xs sm:text-sm leading-relaxed">
          <div className="p-5 rounded-2xl bg-zinc-900/40 border border-zinc-800/80 space-y-3">
            <div className="flex items-center gap-2 text-white font-semibold text-base">
              <HelpCircle className="w-5 h-5 text-zinc-400" />
              <span>Cómo Funciona la Arquitectura de Vidly AI</span>
            </div>
            <p className="text-zinc-400 text-xs">
              Preguntas frecuentes sobre el procesador de vídeo, la inteligencia artificial y el ensamblado de fotogramas.
            </p>
          </div>

          <div className="space-y-3">
            <div className="p-4 rounded-xl bg-zinc-900/30 border border-zinc-800/60 space-y-1.5">
              <h4 className="font-semibold text-white text-xs sm:text-sm">¿Por qué no hay pérdida de resolución ni calidad?</h4>
              <p className="text-zinc-400 text-xs leading-relaxed">
                Vidly AI no es un generador de vídeo sintético que invente fotogramas. La IA actúa como un editor senior planificando los puntos de corte, y nuestro motor nativo FFmpeg realiza cortes directos sobre los fotogramas originales con un crossfade de audio de 40ms para un ensamblado imperceptible.
              </p>
            </div>

            <div className="p-4 rounded-xl bg-zinc-900/30 border border-zinc-800/60 space-y-1.5">
              <h4 className="font-semibold text-white text-xs sm:text-sm">¿Cómo evita cortar frases a la mitad?</h4>
              <p className="text-zinc-400 text-xs leading-relaxed">
                Antes de cortar, se ejecuta un algoritmo de detección de pausas naturales en el habla (`silencedetect`). Los cortes sugeridos por la IA se ajustan milimétricamente al silencio vocal más cercano, evitando interrupciones abruptas en las palabras.
              </p>
            </div>

            <div className="p-4 rounded-xl bg-zinc-900/30 border border-zinc-800/60 space-y-1.5">
              <h4 className="font-semibold text-white text-xs sm:text-sm">¿Puedo obligar a la IA a incluir un minuto específico?</h4>
              <p className="text-zinc-400 text-xs leading-relaxed">
                Sí. En el paso de Ajustes puedes agregar uno o más "Intervalos obligatorios". Nuestro algoritmo los fijará como prioritarios y buscará el resto de los mejores momentos alrededor de ellos.
              </p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
